'use client';

import { type FC, useContext } from 'react';
import { usePathname } from 'next/navigation';
import { LeftNavItem, type LeftNavItemProps } from '@/_components/LeftNavItem';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';
import type { SettingsNavProps } from './SettingsNav.types';

export const SettingsNav: FC<SettingsNavProps> = ({ isHovering }) => {
  const path = usePathname();
  const { preferences } = useContext(FirmSettingsContext);
  const { permissions, isSorabanAdmin } = useContext(UserContext);

  const hasFirmRolesSettingsPermission: boolean = getHasPermission(
    PermissionsMapEnum['firm.edit_firm_roles'],
    permissions,
  );

  const hasFirmBillingPermission: boolean = getHasPermission(
    PermissionsMapEnum['firm.billing'],
    permissions,
  );

  const hasFirmRoleDetailsFlag: boolean = Boolean(
    preferences?._tmp_firm_role_permissions_enabled ||
      preferences?._tmp_firm_role_notifications_enabled,
  );

  const items: Array<LeftNavItemProps> = [
    { isBackToWorkspace: true, isHovering },
    // { isSeparator: true, name: 'Firm Settings' },
    {
      name: 'General',
      icon: 'gear-fine',
      href: '/settings',
      isActive: path === '/settings',
    },

    {
      name: hasFirmRoleDetailsFlag ? 'Users' : 'Users & Roles',
      icon: 'users',
      href: '/settings/users',
      isActive: path === '/settings/users',
    },

    ...(hasFirmRolesSettingsPermission && hasFirmRoleDetailsFlag
      ? [
          {
            name: 'Roles & Permissions',
            href: '/settings/permissions',
            icon: 'shield',
            isActive: path === '/settings/permissions',
          } as LeftNavItemProps,
        ]
      : []),

    ...(hasFirmBillingPermission
      ? [
          {
            name: 'Reports & Audits',
            icon: 'chart-bar-horizontal',
            href: '/settings/reports',
            isActive: path === '/settings/reports',
          } as LeftNavItemProps,
          {
            name: 'Credits & Billing',
            icon: 'receipt',
            href: '/settings/billing',
            isActive: path === '/settings/billing',
          } as LeftNavItemProps,
        ]
      : []),

    {
      name: 'Integrations',
      icon: 'plug',
      href: '/settings/integrations',
      isActive: path === '/settings/integrations',
    },
    {
      name: 'Customization',
      icon: 'monitor',
      href: '/settings/customization',
      isActive: path === '/settings/customization',
    },
    ...(preferences?._tmp_enterprise_tier
      ? [
          {
            name: 'Enterprise Auth',
            icon: 'users',
            isLocked: true,
            href: '/settings/enterprise-auth',
            isActive: path === '/settings/enterprise-auth',
          } as LeftNavItemProps,
          {
            name: 'Granular Permissions',
            icon: 'key',
            isLocked: true,
            href: '/settings/granular-permissions',
            isActive: path === '/settings/granular-firm-user-permissions',
          } as LeftNavItemProps,
          {
            name: 'Document Retention',
            icon: 'folder',
            href: '/settings/document-retention',
            isActive: path === '/settings/document-retention',
          } as LeftNavItemProps,
        ]
      : []),
    ...(isSorabanAdmin
      ? [
          {
            name: 'Soraban Admins Only',
            icon: 'warning',
            href: '/settings/soraban-admins-only',
            isActive: path === '/settings/soraban-admins-only',
          } as LeftNavItemProps,
        ]
      : []),
  ];

  return (
    <>
      {items.map((item, key) => (
        <LeftNavItem {...item} key={key} isActive={item.isActive} />
      ))}
    </>
  );
};
